/**
 * RepositoryAppGuard - validates the `/repositories/:applicationId` URL segment
 * against the active organization before rendering any application-scoped page.
 *
 * The application id comes from the URL while the organization comes from
 * OrganizationContext (localStorage / `?org=`). These resolve independently, so
 * after switching workspaces or using the browser back button the URL can carry
 * an application id that belongs to a *different* organization. The backend
 * lookup is a strict `{id, organization_id}` match, so every app-scoped page
 * would then 404 — some dead-ending on "Application/Finding/Fix not found".
 *
 * This guard heals that centrally: if the URL application id does not belong to
 * the active organization it redirects to the `/repositories` resolver (which
 * lands the user on a valid repository for the active org) instead of letting a
 * child page mount and render a dead-end.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from '../../design-system';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useApplication } from '../../contexts/ApplicationContext';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints, ApiError } from '../../utils/api';

const RepositoryAppGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAuthenticatedApi();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { applications, applicationsLoaded } = useApplication();

  // App-scoped routes look like `/repositories/<id>[/...]`. Parse the id from
  // the path (mirrors ApplicationContext) rather than useParams, since this is a
  // layout route whose own match has no params.
  const applicationId = useMemo(() => {
    const match = location.pathname.match(/^\/repositories\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  // Fast path: the org's (non-archived) repository list is already loaded for
  // the sidebar switcher. If the URL id is in it, it is valid for this org —
  // no extra request, no spinner for the common case.
  const inOrgList =
    applicationsLoaded && !!applicationId && applications.some((a) => a.id === applicationId);

  // Ids not in that list still need confirming, because archived apps are valid
  // but excluded from the list. A direct fetch settles it: 200 → valid (render),
  // 404 → cross-org/stale (redirect).
  const [confirmedId, setConfirmedId] = useState<string | null>(null);

  useEffect(() => {
    if (!applicationId || inOrgList) return;
    if (orgLoading || !currentOrganization?.id || !applicationsLoaded) return;

    let cancelled = false;
    api
      .get(apiEndpoints.applications.get(currentOrganization.id, applicationId))
      .then(() => {
        if (!cancelled) setConfirmedId(applicationId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Stale/cross-org application id in the URL — heal to the resolver,
          // which redirects to a valid repository for the active organization.
          navigate('/repositories', { replace: true });
          return;
        }
        // Transient/other error: don't trap the user behind the guard — render
        // the page and let it surface its own loading/error state.
        setConfirmedId(applicationId);
      });

    return () => {
      cancelled = true;
    };
  }, [api, applicationId, inOrgList, applicationsLoaded, orgLoading, currentOrganization?.id, navigate]);

  // Not an app-scoped route, or the id is already known-valid → render the page.
  if (!applicationId || inOrgList || confirmedId === applicationId) {
    return <Outlet />;
  }

  // Org context settled without an organization — leave routing to the upstream
  // OrganizationRequiredRoute rather than spinning here forever.
  if (!orgLoading && !currentOrganization?.id) {
    return <Outlet />;
  }

  // Validating: waiting on the org repository list or the confirming fetch.
  return (
    <div className="flex items-center justify-center w-full h-full py-20">
      <Spinner size={24} />
    </div>
  );
};

export default RepositoryAppGuard;
